import { useCallback, useEffect, useRef, useState } from "react";
import { getCart, saveCart } from "../services/cart";
import { errorMessage } from "../services";
import { askForPick, lineKey, needsPick, pickComplete, pickLabel, pricedAt } from "../utils/variants";
import { flyToCart } from "../utils/motion";

const CART_KEY = "gn_cart";
// Whose cart the browser copy is: a shopper's id, or nothing for a guest's. Only a guest's cart is added to the
// saved one at login; adding the shopper's own copy again doubled every quantity on each page load.
const OWNER_KEY = "gn_cart_owner";
const MAX_QTY = 99;
const readOwner = () => {
    try {
        return localStorage.getItem(OWNER_KEY) || null;
    } catch {
        return null;
    }
};
const writeOwner = (id) => {
    try {
        if (id) localStorage.setItem(OWNER_KEY, id);
        else localStorage.removeItem(OWNER_KEY);
    } catch {
        // the cart still works; it may only merge once more after a reload
    }
};

const readCart = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(CART_KEY));
        return Array.isArray(saved) ? saved : [];
    } catch (error) {
        return [];
    }
};

const toPayload = (items) =>
    items.map((item) => ({
        productId: item.id,
        qty: item.qty,
        ...(item.size ? { size: item.size } : {}),
        ...(item.color ? { color: item.color } : {}),
        ...(item.text ? { text: item.text } : {}),
        ...(item.photos && item.photos.length ? { photos: item.photos } : {}),
    }));
const signature = (items) => JSON.stringify(toPayload(items));

// Most we can sell of a line: the stock we last saw, never more than 99
const limitFor = (item) => Math.min(MAX_QTY, item.stock > 0 ? item.stock : MAX_QTY);

// Shopping cart state.
// - Guests: the cart lives in the browser (localStorage is only a cache so a refresh keeps it).
// - Signed in: the cart is also saved on the server. On login the guest cart is merged into the
//   saved one, then every change is saved (after a short pause).
// Lines are full product objects plus `qty`, and the shopper's size and colour for a product sold in them (one line
// per size and colour, known by lineKey). The prices shown are what we last loaded; the server re-prices everything
// when the order is placed.
export default function useCart(user, notify) {
    const [items, setItems] = useState(readCart);
    const itemsRef = useRef(items);
    const synced = useRef(false); // true once the guest and saved carts are merged for this login
    const lastSaved = useRef("");
    const userId = user ? user.id : null;

    itemsRef.current = items;

    useEffect(() => {
        localStorage.setItem(CART_KEY, JSON.stringify(items));
    }, [items]);

    // Merge on login / session restore. A guest's cart is added to the saved one; on a page reload the browser copy
    // is the shopper's own, so the saved cart is used as it is.
    const wasSignedIn = useRef(Boolean(userId));
    // A page that opens with the shopper already signed in holds their own copy (also from before the owner was kept)
    const openedSignedIn = useRef(Boolean(userId));
    useEffect(() => {
        synced.current = false;
        if (!userId) {
            // Signed out: the cart was the shopper's, so it is not kept for the next person on this browser
            if (wasSignedIn.current) {
                setItems([]);
                writeOwner(null);
            }
            wasSignedIn.current = false;
            openedSignedIn.current = false;
            return undefined;
        }
        wasSignedIn.current = true;
        const owner = readOwner();
        const ownCopy = owner === userId || (!owner && openedSignedIn.current);
        openedSignedIn.current = false;

        let cancelled = false;
        getCart()
            .then((saved) => {
                const lines = new Map();
                saved.forEach((line) => lines.set(lineKey(line), line));
                // A guest's lines are added (saved lines carry the fresh price/stock); the shopper's own copy is the
                // saved cart already, which wins (it also has what they added on another device)
                if (!ownCopy) {
                    itemsRef.current.forEach((line) => {
                        const existing = lines.get(lineKey(line));
                        lines.set(lineKey(line), existing ? { ...existing, qty: existing.qty + line.qty } : line);
                    });
                }
                const merged = [...lines.values()].map((line) => ({ ...line, qty: Math.min(line.qty, limitFor(line)) }));
                return saveCart(toPayload(merged));
            })
            .then((hydrated) => {
                if (cancelled) return;
                lastSaved.current = signature(hydrated);
                setItems(hydrated);
                writeOwner(userId);
                synced.current = true;
            })
            .catch((error) => {
                if (!cancelled) notify(errorMessage(error));
            });
        return () => {
            cancelled = true;
        };
    }, [userId, notify]);

    // Save every change for a signed-in shopper
    useEffect(() => {
        if (!userId || !synced.current || signature(items) === lastSaved.current) return undefined;
        const timer = window.setTimeout(() => {
            saveCart(toPayload(items))
                .then(() => {
                    lastSaved.current = signature(items);
                })
                .catch((error) => notify(errorMessage(error)));
        }, 500);
        return () => window.clearTimeout(timer);
    }, [items, userId, notify]);

    // pick: { size, color, text, photos } for a product sold in sizes or colours or personalised. Without a complete
    // pick the picker opens instead (components/VariantSheet.js), and adds the product once the shopper has chosen.
    const add = useCallback(
        (product, pick = {}) => {
            if (product.inStock === false) return notify(`${product.name} is out of stock`);
            if (needsPick(product) && !pickComplete(product, pick)) return askForPick(product);
            const text = (pick.text || "").trim();
            const wanted = needsPick(product)
                ? { ...pricedAt(product, pick.size), size: pick.size, color: pick.color, ...(text ? { text } : {}), ...(pick.photos && pick.photos.length ? { photos: pick.photos } : {}) }
                : product;
            const key = lineKey(wanted);
            // Stock is kept for the product, so every size and colour of it counts towards the limit
            const inCart = itemsRef.current.filter((item) => item.id === product.id).reduce((sum, item) => sum + item.qty, 0);
            const limit = limitFor(itemsRef.current.find((item) => item.id === product.id) || product);
            if (inCart + 1 > limit) return notify(`Only ${limit} of ${product.name} available`);

            setItems((current) => {
                const found = current.find((item) => lineKey(item) === key);
                return found
                    ? current.map((item) => (lineKey(item) === key ? { ...item, qty: item.qty + 1 } : item))
                    : [...current, { ...wanted, qty: 1 }];
            });
            const label = pickLabel(wanted);
            notify(`${product.name}${label ? ` (${label})` : ""} added to cart`);
            flyToCart(product.img);
            return undefined;
        },
        [notify]
    );

    // key: the line's lineKey (the product id for a product without sizes or colours)
    const updateQty = useCallback(
        (key, qty) => {
            const line = itemsRef.current.find((item) => lineKey(item) === key);
            if (!line) return;
            if (qty < 1) {
                setItems((current) => current.filter((item) => lineKey(item) !== key));
                return;
            }
            const others = itemsRef.current.filter((item) => item.id === line.id && lineKey(item) !== key).reduce((sum, item) => sum + item.qty, 0);
            if (qty + others > limitFor(line)) {
                notify(`Only ${limitFor(line)} of ${line.name} available`);
                return;
            }
            setItems((current) => current.map((item) => (lineKey(item) === key ? { ...item, qty } : item)));
        },
        [notify]
    );

    const remove = useCallback((key) => setItems((current) => current.filter((item) => lineKey(item) !== key)), []);
    const clear = useCallback(() => setItems([]), []);

    return { items, count: items.reduce((sum, item) => sum + item.qty, 0), add, updateQty, remove, clear };
}
